/**
 * <s-product-image-zoom> — PDP full-screen photo viewer.
 *
 * Gallery buttons open one native dialog declaratively via commandfor/show-modal.
 * The dialog holds every photo in a horizontal scroll-snap track, so shoppers can swipe
 * (or use the side arrows / arrow keys) through all photos. This component:
 *   - jumps the track to the photo that was tapped when the dialog opens,
 *   - wires the prev/next arrows and arrow keys,
 *   - keeps the "2 / 8" counter in sync,
 *   - warms the tapped photo's high-resolution image on hover/focus.
 *
 * Required markup:
 *   [data-zoom-preload][data-zoom-target] — gallery trigger button; data-zoom-target is the photo's media id.
 *   dialog[data-zoom-dialog]              — the viewer dialog.
 *   [data-zoom-track]                     — horizontal scroll-snap container inside the dialog.
 *   [data-zoom-slide="<media id>"]        — one per photo, each with img[data-zoom-image].
 *   [data-zoom-prev] / [data-zoom-next]   — optional arrow buttons (wrap around).
 *   [data-zoom-current]                   — optional element showing the 1-based position.
 */
class SProductImageZoom extends HTMLElement {
  #ac;
  #track;
  #dialog;
  #preloaded = new Set();
  #preloadLinks = [];

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('pointerover', this.#handlePreloadIntent, opts);
    this.addEventListener('focusin', this.#handlePreloadIntent, opts);
    this.addEventListener('click', this.#handleOpen, opts);

    this.#track = this.querySelector('[data-zoom-track]');
    this.#dialog = this.querySelector('dialog[data-zoom-dialog]');
    if (!this.#track) return;

    this.#track.addEventListener('scroll', this.#updateCounter, { signal: this.#ac.signal, passive: true });
    this.querySelector('[data-zoom-prev]')?.addEventListener('click', () => this.#step(-1), opts);
    this.querySelector('[data-zoom-next]')?.addEventListener('click', () => this.#step(1), opts);
    this.#dialog?.addEventListener('keydown', this.#handleKeydown, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();

    for (const link of this.#preloadLinks) {
      link.remove();
    }
    this.#preloadLinks = [];
    this.#preloaded.clear();
  }

  #slides() {
    return this.#track ? [...this.#track.querySelectorAll('[data-zoom-slide]')] : [];
  }

  #slideFor(id) {
    return this.#slides().find((slide) => slide.dataset.zoomSlide === id) || null;
  }

  #handleOpen = (event) => {
    if (!(event.target instanceof Element)) return;
    const source = event.target.closest('[data-zoom-target]');
    if (!source || !this.contains(source)) return;

    const id = source.dataset.zoomTarget;
    this.#preload(source);
    // The dialog opens from the button's command; wait for it to be visible, then jump to the tapped photo.
    requestAnimationFrame(() => requestAnimationFrame(() => this.#jumpTo(id)));
  };

  #jumpTo(id) {
    const slide = this.#slideFor(id);
    if (!slide || !this.#track) return;
    this.#track.scrollTo({ left: slide.offsetLeft, behavior: 'instant' });
    this.#updateCounter();
  }

  #index() {
    if (!this.#track) return 0;
    const width = this.#track.clientWidth || 1;
    return Math.round(Math.abs(this.#track.scrollLeft) / width);
  }

  #step(direction) {
    const slides = this.#slides();
    const count = slides.length;
    if (count < 2) return;
    const next = (this.#index() + direction + count) % count;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#track.scrollTo({ left: slides[next].offsetLeft, behavior: reduceMotion ? 'instant' : 'smooth' });
  }

  #handleKeydown = (event) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    event.preventDefault();
    const rtl = getComputedStyle(this).direction === 'rtl';
    const forward = event.key === 'ArrowRight' ? !rtl : rtl;
    this.#step(forward ? 1 : -1);
  };

  #updateCounter = () => {
    const counter = this.querySelector('[data-zoom-current]');
    if (counter) counter.textContent = String(this.#index() + 1);
  };

  #handlePreloadIntent = (event) => {
    if (!(event.target instanceof Element)) return;

    const source = event.target.closest('[data-zoom-preload]');
    if (!source || !this.contains(source)) return;

    this.#preload(source);
  };

  #preload(source) {
    const id = source.dataset.zoomTarget;
    if (!id || this.#preloaded.has(id)) return;

    const image = this.#slideFor(id)?.querySelector('img[data-zoom-image]');
    if (!(image instanceof HTMLImageElement)) return;

    this.#preloaded.add(id);
    image.loading = 'eager';
    image.fetchPriority = 'high';

    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = image.src;
    if (image.srcset) link.setAttribute('imagesrcset', image.srcset);
    if (image.sizes) link.setAttribute('imagesizes', image.sizes);
    document.head.append(link);
    this.#preloadLinks.push(link);

    if (typeof image.decode === 'function') {
      image.decode().catch(() => {});
    }
  }
}

if (!customElements.get('s-product-image-zoom')) {
  customElements.define('s-product-image-zoom', SProductImageZoom);
}
